import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import queryString from 'query-string';

import {
  getRecruiterStatusName,
  getRecruiterOutreachDates,
  getRecruiterOutreachChannel,
  isRecruiterRequest,
} from 'utils/recruiterUtils';
import {
  getRecruiterBankingInfoFile,
  getRecruiterDetailsByKey,
  getRecruiterEntityInfoFile,
} from 'redux/reducers/recruiterSlice';
import {
  OutreachEnum,
  ResourceDownloadResponse,
  BOUserStatusEnum,
  StageStatusEnum,
  ProfileTypeEnum,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import BaseLabel from 'components/wrappers/BaseLabel';
import { LoadRecruiterDetailsOptions } from './types';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import RecruiterProgressBar from './RecruiterProgressBar';
import { FullBOUserResponseModel } from 'api/adminApiClient';
import useQueryMatch from 'hooks/useQueryMatch';
import BaseAlert from 'components/wrappers/BaseAlert';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import ActivitiesDetailsSection from 'components/ActivitiesDetailsSection';
import { ActivitiesSectionPageTypeEnum } from 'components/ActivitiesDetailsSection/enums';
import RecruiterPrimaryInfoSection from './RecruiterPrimaryInfoSection';
import RecruiterAdditionalInfoSection from './RecruiterAdditionalInfoSection';
import { RecruiterDetailSection } from './enums';
import MemberLogo from 'components/MemberLogo';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import DownloadClientAgreement from 'features/client/ClientDetailsPage/DownloadClientAgreement';
import { DownloadAgreementTypeEnum } from 'features/client/ClientDetailsPage/DownloadClientAgreement/DownloadClientAgreement';

import styles from './RecruiterDetailsPage.module.less';

type ParamsProps = {
  memberKey: string;
};

const DEFAULT_SECTION = RecruiterDetailSection.PrimaryInfo;

const getPageSections = (isCorporateTA?: boolean) => {
  return isCorporateTA
    ? Object.values(RecruiterDetailSection)
    : [DEFAULT_SECTION];
};

export default function RecruiterDetailsPage() {
  const { memberKey } = useParams<ParamsProps>();
  const dispatch = useAppDispatch();
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [recruiterDetails, setRecruiterDetails] =
    useState<FullBOUserResponseModel>();
  const [bankingInfoFile, setBankingInfoFile] =
    useState<ResourceDownloadResponse>();
  const [entityInfoFile, setEntityInfoFile] =
    useState<ResourceDownloadResponse>();
  const isAdminRole = useAppSelector(isAdminSelector);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const query = queryString.parse(location.search);
  const isCorporateTADetails =
    recruiterDetails?.profileType === ProfileTypeEnum.Corporate;
  const pageSectionMatch = useQueryMatch(getPageSections(isCorporateTADetails));

  const loadBankingInfoFile = useCallback(
    async (memberId: string) => {
      const result = await dispatch(getRecruiterBankingInfoFile(memberId));

      if (getRecruiterBankingInfoFile.fulfilled.match(result)) {
        setBankingInfoFile(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [dispatch],
  );

  const loadEntityInfoFile = useCallback(
    async (memberId: string) => {
      const result = await dispatch(getRecruiterEntityInfoFile(memberId));

      if (getRecruiterEntityInfoFile.fulfilled.match(result)) {
        setEntityInfoFile(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!pageSectionMatch) {
      navigate(`${location.pathname}?section=${DEFAULT_SECTION}`, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSectionMatch]);

  const loadRecruiterDetails = useCallback(
    async (options?: LoadRecruiterDetailsOptions) => {
      if (!memberKey) return;

      const result = await dispatch(getRecruiterDetailsByKey(memberKey));

      if (getRecruiterDetailsByKey.fulfilled.match(result)) {
        if (
          !!result.payload?.businessInfo?.entityInformation?.entityInfoFileKey
        ) {
          options?.shouldLoadEntityFile &&
            (await loadEntityInfoFile(result.payload?.userId as string));
        } else {
          setEntityInfoFile(undefined);
        }

        if (!!result.payload?.bankingInformation?.bankInfoFileKey) {
          options?.shouldLoadBankingFile &&
            (await loadBankingInfoFile(result.payload?.userId as string));
        } else {
          setBankingInfoFile(undefined);
        }

        setRecruiterDetails(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }

      setIsInitialLoaded(true);
    },
    [dispatch, memberKey, loadEntityInfoFile, loadBankingInfoFile],
  );

  useEffect(() => {
    loadRecruiterDetails({
      shouldLoadEntityFile: true,
      shouldLoadBankingFile: true,
    });
  }, [loadRecruiterDetails]);

  if (!isInitialLoaded) return <Spin size="large" />;

  if (!recruiterDetails) return null;

  const { status, stageStatus, businessInfo, corporateInfo, memberLogoUrl } =
    recruiterDetails;

  const isRequest = isRecruiterRequest(
    status as BOUserStatusEnum,
    stageStatus as StageStatusEnum,
  );
  const recruiterStatus = getRecruiterStatusName(status);
  const recruiterOutreachDates = getRecruiterOutreachDates(
    businessInfo?.outreach?.dates,
  );
  const recruiterOutreachChannel = getRecruiterOutreachChannel(
    businessInfo?.outreach?.outreachType,
  );
  const recruiterOutreachValue =
    businessInfo?.outreach?.outreachType === OutreachEnum.Phone
      ? businessInfo?.outreach?.phone
      : businessInfo?.outreach?.email;

  const getPageLabel = () => {
    return isRequest ? 'Member request details' : 'Member details';
  };

  const getInfoSection = () => {
    switch (query.section) {
      case RecruiterDetailSection.AdditionalInfo:
        return <RecruiterAdditionalInfoSection corporateInfo={corporateInfo} />;

      case RecruiterDetailSection.PrimaryInfo:
      default:
        return (
          <RecruiterPrimaryInfoSection
            recruiterDetails={recruiterDetails}
            entityInfo={businessInfo?.entityInformation}
            entityInfoFile={entityInfoFile}
            bankingInfoFile={bankingInfoFile}
            loadRecruiterDetails={loadRecruiterDetails}
          />
        );
    }
  };

  return (
    <div ref={containerRef} className="page-wrapper scroll-box">
      <div className="page-holder">
        <div className={styles.progressBarWrapper}>
          <RecruiterProgressBar
            isRequest={isRequest}
            recruiter={recruiterDetails}
            loadRecruiterDetails={loadRecruiterDetails}
          />
        </div>

        <BasePageLabel label={getPageLabel()} />

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <div className="page-head align-start">
              <div className="page-head__left align-start">
                <div className="page-head__logo">
                  <MemberLogo member={recruiterDetails} url={memberLogoUrl} />
                </div>
                <div className="page-title page-head__title">
                  {businessInfo?.name}
                  <div>
                    <BaseLabel status={recruiterStatus} />
                  </div>
                  {recruiterOutreachDates && recruiterOutreachValue && (
                    <div className={styles.outreachDate}>
                      Outreach can be performed via {recruiterOutreachChannel}:
                      {recruiterOutreachValue} on {recruiterOutreachDates}.
                    </div>
                  )}
                </div>
              </div>
              {recruiterDetails.businessInfo?.priceAgreement?.supportingFiles
                ?.length ? (
                <DownloadClientAgreement
                  listOfFiles={
                    recruiterDetails.businessInfo?.priceAgreement
                      ?.supportingFiles
                  }
                  id={recruiterDetails.userId}
                  agreementDetailsType={
                    ServiceAgreementTypeEnum.MemberAgreement
                  }
                  downloadAgreementType={DownloadAgreementTypeEnum.Member}
                />
              ) : (
                <></>
              )}
            </div>

            {getInfoSection()}
          </div>

          {isAdminRole && (
            <>
              <ActivitiesDetailsSection
                scrollRef={containerRef}
                pageType={ActivitiesSectionPageTypeEnum.Member}
                entityId={recruiterDetails?.companyId as string}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
